import { Toast } from 'antd-mobile';
import { KVStore } from '../utils/KVStore';
import {PhotoType, PositionType} from "../pages/delivery/services/PhotoService";
import {GeoPosition} from "../pages/delivery/services/GeolocationService";
import { fileUpload } from '../utils/storageUtils';
import i18n from '../i18n';

// 定义保存数据的接口
interface StorageItem {
  id: string; // 唯一标识符
  data: any; // 实际存储的数据
  timestamp: number; // 存储时间戳
}

export const FILE_UPLOAD_FAILED_KEY = '__FILE_UPLOAD_FAILED__';

// 定义配送订单完整数据结构
export interface FailedDeliveryOrder {
  trackingNumber: string;
  stopNumber?: string;
  subStopNumber?: string | null;
  updatedAt: number;
  photos: Array<{
    type: PositionType;
    photo: PhotoType;
  }>;
  location: GeoPosition | null;
  id: number;
  exceptionReasonCode?: string;
  /** 异常二级原因 code 列表（如地址异常下选中的具体原因），上传时需传给后端 */
  exceptionSubReasons?: string[] | null;
  type: 'failed' | 'delivered';
  // ✅ 新增字段：完成时的GPS位置（点击"完成"按钮时获取）
  completionLocation?: GeoPosition | null;
  deliveredAt?: string;
  /** 妥投方式 code */
  deliveryMethod?: string | null;
}

// 存储类型前缀
const DELIVERY_STORAGE_KEY = 'delivery_order';

/**
 * 将Base64字符串转换为File对象
 * @param base64Data Base64字符串
 * @returns Promise<File> 文件对象
 */
export const base64ToFile = async (base64Data: string,filename: string): Promise<File> => {
  // 从Base64中提取数据部分和MIME类型
  const arr = base64Data.split(',');
  const mime = arr[0].match(/:(.*?);/)![1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  
  // 创建文件对象
  console.log('creating file from base64, filename:', filename);
  return new File([u8arr], filename, { 
    type: mime,
    lastModified: Date.now()
  });
};

export const uploadFailedPhotos = async (photos: PhotoType[], tag: string): Promise<string[]> => {
  console.log('uploadFailedPhotos:', photos.map(item => item.objectKey));

  const uploadOnePhoto = async (objectUrl: string): Promise<string> => {
    try {
      return (await fileUpload(await base64ToFile(objectUrl,Date.now() + '.webp'), tag)).objectKey;
    } catch (error) {
      // 将上传错误转换为网络异常
      const networkError = new Error(error instanceof Error ? error.message : '图片上传失败');
      (networkError as any).networkError = true;
      throw networkError;
    }
  };

  return await Promise.all(photos.map(async (photo) => {
    if (photo.objectKey !== FILE_UPLOAD_FAILED_KEY) {
      return photo.objectKey;
    }

    return await uploadOnePhoto(photo.url);
  }));
};

/**
 * 带超时控制的图片上传方法
 * @param photos 需要上传的图片数组
 * @param timeoutMs 超时时间（毫秒），默认10秒
 * @param showToast 是否显示上传进度提示，默认true
 * @returns Promise<string[]> 上传成功后的图片key数组
 */
export const uploadPhotosWithTimeout = async (
  photos: PhotoType[], 
  tag: string,
  timeoutMs: number = 10000,
  showToast: boolean = true
): Promise<string[]> => {
  let uploadingToast: any = null;
  
  if (showToast) {
    uploadingToast = Toast.show({
      content: i18n.t('pages.delivery.uploading'),
      position: 'center',
      duration: 0,
    });
  }

  try {
    // 创建超时 Promise
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        const error = new Error(i18n.t('pages.delivery.uploadTimeout') as string);
        // 添加 networkError 属性，使其被识别为网络异常
        (error as any).networkError = true;
        reject(error);
      }, timeoutMs);
    });

    // 创建上传 Promise
    const uploadPromise = uploadFailedPhotos(photos, tag);

    // 使用 Promise.race 来实现超时控制
    const photoKeys = await Promise.race([uploadPromise, timeoutPromise]);

    // 上传成功，关闭 Toast
    if (uploadingToast) {
      uploadingToast.close();
    }

    return photoKeys;
  } catch (error) {
    // 上传失败或超时，关闭 Toast
    if (uploadingToast) {
      uploadingToast.close();
    }
    throw error;
  }
};

/**
 * 带实时更新的图片上传方法 - 支持在每张图片上传成功后立即更新状态
 * @param photos 需要上传的图片数组
 * @param tag 上传标签
 * @param onPhotoUploaded 单张图片上传成功回调 (index: number, objectKey: string) => void
 * @param timeoutMs 超时时间（毫秒），默认10秒
 * @param showToast 是否显示上传进度提示，默认true
 * @returns Promise<string[]> 上传成功后的图片key数组
 */
export const uploadPhotosWithRealtimeUpdate = async (
    photos: PhotoType[],
    tag: string,
    onPhotoUploaded?: (index: number, objectKey: string) => void,
    timeoutMs: number = 10000,
    showToast: boolean = true
): Promise<string[]> => {
  let uploadingToast: any = null;

  if (showToast) {
    uploadingToast = Toast.show({
      content: i18n.t('pages.delivery.uploading'),
      position: 'center',
      duration: 0,
    });
  }

  try {
    // 创建超时 Promise
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        const error = new Error(i18n.t('pages.delivery.uploadTimeout') as string);
        // 添加 networkError 属性，使其被识别为网络异常
        (error as any).networkError = true;
        reject(error);
      }, timeoutMs);
    });

    // 创建支持实时更新的上传 Promise
    const uploadPromise = uploadPhotosWithCallback(photos, tag, onPhotoUploaded);

    // 使用 Promise.race 来实现超时控制
    const photoKeys = await Promise.race([uploadPromise, timeoutPromise]);

    // 上传成功，关闭 Toast
    if (uploadingToast) {
      uploadingToast.close();
    }

    return photoKeys;
  } catch (error) {
    console.error('❌ 批量上传失败:', error);

    // 上传失败或超时，关闭 Toast
    if (uploadingToast) {
      uploadingToast.close();
    }
    throw error;
  }
};

/**
 * 支持回调的图片上传方法
 */
const uploadPhotosWithCallback = async (
    photos: PhotoType[],
    tag: string,
    onPhotoUploaded?: (index: number, objectKey: string) => void
): Promise<string[]> => {

  const uploadOnePhoto = async (objectUrl: string): Promise<string> => {
    return (await fileUpload(await base64ToFile(objectUrl, Date.now() + '.webp'), tag)).objectKey;
  };

  const results: string[] = [];

  for (let i = 0; i < photos.length; i++) {
    const photo = photos[i];

    if (photo.objectKey !== FILE_UPLOAD_FAILED_KEY) {
      results.push(photo.objectKey);
    } else {
      try {
        const objectKey = await uploadOnePhoto(photo.url);
        results.push(objectKey);

        // 立即回调更新状态
        if (onPhotoUploaded) {
          onPhotoUploaded(i, objectKey);
        }
      } catch (error) {
        console.error(`❌ 图片 ${i} 上传失败:`, error);
        throw error;
      }
    }
  }

  return results;
};


/**
 * 配送数据服务
 * 用于管理配送订单的数据存储、检索和同步
 */
class DeliveryDataService {

  private kvStore: KVStore;

  constructor() {
    this.kvStore = new KVStore(DELIVERY_STORAGE_KEY);
  }

  /**
   * 保存配送订单数据
   * @param order 配送订单数据
   * @returns 保存成功返回true，失败返回false
   */
  async saveDeliveryOrder(order: FailedDeliveryOrder): Promise<boolean> {
    try {
      // 确保有更新时间
      if (!order.updatedAt) {
        order.updatedAt = Date.now();
      }

      const storageItem: StorageItem = {
        id: order.trackingNumber,
        data: order,
        timestamp: order.updatedAt
      };

      // 保存到本地存储
      return await this.kvStore.setItem(order.trackingNumber, storageItem);
    } catch (error) {
      console.error('Failed to save delivery order:', error);
      Toast.show({
        content: '保存订单数据失败',
        position: 'bottom',
      });
      return false;
    }
  }
  
  /**
   * 获取所有配送订单数据
   * @returns 返回所有订单数据数组
   */
  async getAllDeliveryOrders(): Promise<FailedDeliveryOrder[]> {
    try {
        const storageItems = await this.kvStore.getAllItems<StorageItem>();
        // 按时间戳排序，最新的在前
        return storageItems
          .map(item => item.data as FailedDeliveryOrder)
          .sort((a, b) => b.updatedAt - a.updatedAt);
    } catch (error) {
      console.error('Failed to get all delivery orders:', error);
      return [];
    }
  }
  
  /**
   * 删除配送订单数据
   * @param id 订单ID
   * @returns 删除成功返回true，失败返回false
   */
  async deleteDeliveryOrder(trackingNumber: string): Promise<boolean> {
    try {
        return await this.kvStore.removeItem(trackingNumber);
    } catch (error) {
      console.error('Failed to delete delivery order:', error);
      return false;
    }
  }
  
  /**
   * 获取配送订单总数
   * @returns 返回订单总数
   */
  async getDeliveryOrderCount(): Promise<number> {
    try {
      return await this.kvStore.size();
    } catch (error) {
      console.error('Failed to get delivery order count:', error);
      return 0;
    }
  }
}

const deliveryDataService = new DeliveryDataService();

export default deliveryDataService; 